import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Clock, Trash2, Loader2, Calendar, User, Users, Download } from "lucide-react";
import { toast } from "sonner";
import PunchButton from "@/components/app/PunchButton";

const todayISO = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7); // YYYY-MM

function calcHours(start, end, breakMin) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let min = eh * 60 + em - (sh * 60 + sm) - (Number(breakMin) || 0);
  if (min < 0) min += 24 * 60;
  return Math.round((min / 60) * 100) / 100;
}

export default function Timesheet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(thisMonth());
  const [employees, setEmployees] = useState([]);
  const [filterUser, setFilterUser] = useState("all"); // admin filter
  const [form, setForm] = useState({
    user_id: "",
    date: todayISO(),
    start_time: "08:00",
    end_time: "17:00",
    break_minutes: 60,
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [listRes, sumRes] = await Promise.all([
        api.get("/timesheet", { params: { month, user_id: isAdmin && filterUser !== "all" ? filterUser : undefined } }),
        api.get("/timesheet/summary", { params: { month } }),
      ]);
      setEntries(listRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.role === "dipendente")));
  }, [isAdmin]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, filterUser]);

  const previewHours = useMemo(() => calcHours(form.start_time, form.end_time, form.break_minutes), [form.start_time, form.end_time, form.break_minutes]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        break_minutes: Number(form.break_minutes) || 0,
        note: form.note,
      };
      if (isAdmin && form.user_id) payload.user_id = form.user_id;
      await api.post("/timesheet", payload);
      toast.success("Giornata registrata");
      setForm((f) => ({ ...f, note: "" }));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const penalize = async (e) => {
    const h = window.prompt("Ore di penalità da togliere da questa giornata (0 per annullare)", e.penalty_hours || "");
    if (h === null) return;
    const note = Number(h) > 0 ? window.prompt("Motivo della penalità", e.penalty_note || "") || "" : "";
    try {
      await api.put(`/timesheet/${e.id}`, { penalty_hours: Math.max(0, Number(h) || 0), penalty_note: note });
      toast.success("Penalità salvata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questa giornata?")) return;
    try {
      await api.delete(`/timesheet/${id}`);
      toast.success("Eliminata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="timesheet-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Presenze</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            {isAdmin ? "Ore Dipendenti" : "Le Mie Ore"}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            {isAdmin
              ? "Dettaglio timbrature entrata e uscita, pause e ore totali di ogni dipendente — mese per mese."
              : "Timbra entrata e uscita col pulsante qui sotto. Una volta chiusa la giornata non è più modificabile — si riattiverà domani alle 00:00."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44 h-11 bg-white"
            data-testid="timesheet-month-input"
          />
          {isAdmin && (
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="w-56 h-11 bg-white" data-testid="timesheet-user-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i dipendenti</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isAdmin && (
            <Button
              onClick={async () => {
                try {
                  const params = { month };
                  if (filterUser !== "all") params.user_id = filterUser;
                  const res = await api.get("/timesheet/export", { params, responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `presenze_${month}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("CSV scaricato — pronto per il commercialista");
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
              className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="timesheet-export-csv-button"
            >
              <Download className="w-4 h-4 mr-2" /> Esporta CSV
            </Button>
          )}
        </div>
      </header>

      {/* Admin summary */}
      {isAdmin && summary && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="timesheet-summary">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-indigo-600" />
            <h2 className="font-display font-bold text-slate-900">Riepilogo del mese</h2>
            <span className="ml-auto text-xs text-slate-400 font-mono">
              TOTALE: <b className="text-slate-900">{summary.grand_total.toFixed(1)}h</b>
            </span>
          </div>
          {summary.rows.length === 0 ? (
            <div className="text-sm text-slate-400 italic">Nessuna ora registrata questo mese.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {summary.rows.map((r) => (
                <div key={r.user_id} className="py-3 flex items-center gap-3" data-testid={`summary-row-${r.user_id}`}>
                  <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm">
                    {r.user_name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{r.user_name}</div>
                    <div className="text-xs text-slate-400 font-mono">{r.days} giornat{r.days === 1 ? "a" : "e"}</div>
                  </div>
                  <div className="font-display font-extrabold text-xl text-slate-900 tabular-nums">
                    {r.total_hours.toFixed(1)}<span className="text-sm text-slate-400 ml-1">h</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!isAdmin && <PunchButton />}

      {/* Add form - admin only (employees use PunchButton for clock-in/out) */}
      {isAdmin && (
      <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="timesheet-form">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="w-5 h-5 text-sky-600" />
          <h2 className="font-display font-bold text-slate-900">
            Aggiungi giornata (per un dipendente)
          </h2>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-5">
            <div>
              <Label>Dipendente</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="timesheet-form-user"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="timesheet-form-date" />
            </div>
            <div>
              <Label>Ora inizio</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="timesheet-form-start" />
            </div>
            <div>
              <Label>Ora fine</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required data-testid="timesheet-form-end" />
            </div>
            <div>
              <Label>Pausa (min)</Label>
              <Input type="number" min="0" max="300" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} data-testid="timesheet-form-break" />
            </div>
          </div>
          <div>
            <Label>Note (opz.)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Trasloco Rossi · Milano…" data-testid="timesheet-form-note" />
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-sm text-slate-500">
              Totale: <b className="text-slate-900 text-lg font-display font-extrabold">{previewHours.toFixed(1)}h</b>
            </div>
            <Button type="submit" disabled={saving || !form.user_id} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="timesheet-submit-button">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Registra Giornata
            </Button>
          </div>
        </form>
      </section>
      )}

      {/* Entries list */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-emerald-600" />
          <h2 className="font-display font-bold text-slate-900">
            {isAdmin ? (filterUser === "all" ? "Dettagli timbrature (entrata · uscita · pausa)" : "Giornate dipendente") : "Mie Giornate"} · {month}
          </h2>
          <span className="ml-auto text-xs text-slate-400 font-mono">{entries.length} vo{entries.length === 1 ? "ce" : "ci"}</span>
        </div>
        {entries.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">Nessuna giornata registrata.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="timesheet-entries">
            {entries.map((e) => {
              const closed = Boolean(e.end_time);
              const canDelete = isAdmin || (!closed && e.user_id === user?.id);
              return (
                <div key={e.id} className="py-3 flex items-center gap-3" data-testid={`entry-${e.id}`}>
                  <div className="w-16 text-center shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                      {new Date(e.date).toLocaleDateString("it-IT", { weekday: "short" })}
                    </div>
                    <div className="font-display font-extrabold text-slate-900">
                      {new Date(e.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    {isAdmin && <div className="text-xs font-semibold text-indigo-700 truncate">{e.user_name}</div>}
                    <div className="text-sm font-mono text-slate-700 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
                      <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 text-[11px] font-bold text-emerald-800">
                        ↗ {e.start_time || "—"}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-bold ${e.end_time ? "bg-rose-50 border border-rose-200 text-rose-800" : "bg-amber-50 border border-amber-200 text-amber-800 animate-pulse"}`}>
                        ↙ {e.end_time || "aperta"}
                      </span>
                      {e.break_minutes > 0 && <span className="text-slate-500 text-[11px]">pausa <b>{e.break_minutes}′</b></span>}
                      {e.punched && <span className="text-[10px] uppercase tracking-widest text-sky-700 font-bold bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">Timbrata</span>}
                      {closed && !isAdmin && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">Chiusa</span>}
                    </div>
                    {e.note && <div className="text-xs text-slate-500 mt-0.5 italic truncate">{e.note}</div>}
                    {e.penalty_hours > 0 && <div className="text-xs text-rose-600 mt-0.5" data-testid={`penalty-${e.id}`}>Penalità −{e.penalty_hours}h{e.penalty_note ? ` · ${e.penalty_note}` : ""}</div>}
                  </div>
                  <div className="font-display font-extrabold text-lg text-slate-900 tabular-nums shrink-0">
                    {e.hours.toFixed(1)}<span className="text-xs text-slate-400 ml-0.5">h</span>
                    {e.penalty_hours > 0 && <div className="text-[11px] text-rose-600 font-semibold text-right" data-testid={`net-hours-${e.id}`}>netto {Math.max(0, e.hours - e.penalty_hours).toFixed(1)}h</div>}
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => penalize(e)}
                      className="px-2 py-1 rounded-lg text-[11px] font-semibold text-rose-600 hover:bg-rose-50"
                      data-testid={`penalty-entry-${e.id}`}
                    >
                      Penalità
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => remove(e.id)}
                      className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                      data-testid={`delete-entry-${e.id}`}
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
